import useSWR from 'swr';
import api from '../../../services/api';
import type { EmailAiPromptMeta } from '../components/ai-assistant/emailAiAssistant.types';
import type { AiModelOption } from '../../configuration-settings';

export function emailAiPromptsKey(menuContext: 'compose' | 'read'): string {
  return `/ai/email/prompts?menuContext=${encodeURIComponent(menuContext)}`;
}

export const EMAIL_AI_MODELS_KEY = '/ai/email/models';

export function buildEmailChatHistoryKey(params: {
  emailId?: number;
  partitionAt?: string | null;
  composeSessionId?: string;
}): string {
  const qs = new URLSearchParams();
  if (params.emailId != null) qs.set('emailId', String(params.emailId));
  if (params.partitionAt) qs.set('partitionAt', params.partitionAt);
  if (params.composeSessionId?.trim()) qs.set('composeSessionId', params.composeSessionId.trim());
  return `/ai/email/chat-history?${qs.toString()}`;
}

export interface EmailAiChatHistoryMessage {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
}

export interface EmailAiChatHistoryResult {
  conversation: {
    id: number;
    title: string;
    modelId: string | null;
    sourceType: string | null;
    sourceRef: string | null;
    updatedAt: string;
  } | null;
  messages: EmailAiChatHistoryMessage[];
}

async function fetchPrompts(url: string): Promise<EmailAiPromptMeta[]> {
  const res = await api.get<{ prompts: EmailAiPromptMeta[] }>(url);
  return res.data.prompts ?? [];
}

async function fetchModels(url: string): Promise<AiModelOption[]> {
  const res = await api.get<{ models: AiModelOption[] }>(url);
  return res.data.models ?? [];
}

async function fetchChatHistory(url: string): Promise<EmailAiChatHistoryResult> {
  const res = await api.get<EmailAiChatHistoryResult>(url);
  return res.data;
}

export function useEmailAiPrompts(menuContext: 'compose' | 'read', enabled: boolean) {
  return useSWR(enabled ? emailAiPromptsKey(menuContext) : null, fetchPrompts, {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
  });
}

export function useEmailAiModels(enabled: boolean) {
  return useSWR(enabled ? EMAIL_AI_MODELS_KEY : null, fetchModels, {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
  });
}

export function useEmailAiChatHistory(
  params: {
    emailId?: number;
    partitionAt?: string | null;
    composeSessionId?: string;
  },
  enabled: boolean,
) {
  const key = enabled ? buildEmailChatHistoryKey(params) : null;
  return useSWR(key, fetchChatHistory, {
    revalidateOnFocus: false,
    dedupingInterval: 5_000,
  });
}
